import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar as CalendarIcon, Clock, MapPin, User, Store, AlertTriangle, CheckCircle, Trash2, Video, Phone, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { useAppointmentsStore, Appointment, AppointmentStatus, AppointmentTipo } from '@/store/appointmentsStore';
import { useLeadsStore } from '@/store/leadsStore';
import { useLojasStore } from '@/store/lojasStore';
import { useUsersStore } from '@/store/usersStore';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { 
  getAppointmentDurationSeconds, 
  getAppointmentDurationMinutes, 
  writeDuration,
  formatDurationLabel,
  calculateAppointmentEndTime,
  splitDurationToHms,
  parseDurationFromHms,
  validateAndNormalizeDuration
} from '@/lib/appointment-duration';

interface AppointmentModalProps {
  isOpen?: boolean;
  open?: boolean;
  onClose: () => void;
  appointmentId?: string | null;
  initialDateTime?: Date | null;
  initialLeadId?: string | null;
  initialLead?: any;
  leadId?: string | null;
  leadName?: string | null;
  lojaId?: string | null;
  onSaved?: () => void;
}

export function AppointmentModal({
  isOpen,
  open,
  onClose,
  appointmentId,
  initialDateTime,
  initialLeadId,
  initialLead,
  leadId: propLeadId,
  leadName: propLeadName,
  lojaId: propLojaId,
  onSaved
}: AppointmentModalProps) {
  const isModalOpen = isOpen ?? open ?? false;
  const targetLeadId = initialLeadId || propLeadId;
  const { appointments, createAppointment, updateAppointment, deleteAppointment, updateStatus } = useAppointmentsStore();
  const { leads, fetchLeads } = useLeadsStore();
  const { lojas, fetchLojas } = useLojasStore();
  const { users, fetchUsers } = useUsersStore();

  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Form states
  const [leadId, setLeadId] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [tipo, setTipo] = useState<AppointmentTipo>('visita_loja');
  const [titulo, setTitulo] = useState('');
  const [agendadoPara, setAgendadoPara] = useState('');
  
  // Duração canônica em segundos e HMS
  const [duracaoSegundos, setDuracaoSegundos] = useState<number>(3600);
  const [hmsHours, setHmsHours] = useState<number>(1);
  const [hmsMinutes, setHmsMinutes] = useState<number>(0);
  const [hmsSeconds, setHmsSeconds] = useState<number>(0);
  const [customDurationMode, setCustomDurationMode] = useState<boolean>(false);

  const [lojaId, setLojaId] = useState('');
  const [responsavelUserId, setResponsavelUserId] = useState('');
  const [local, setLocal] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [status, setStatus] = useState<AppointmentStatus>('agendado');

  useEffect(() => {
    if (isModalOpen) {
      fetchLeads();
      fetchLojas();
      fetchUsers(true);
    }
  }, [isModalOpen, fetchLeads, fetchLojas, fetchUsers]);

  const updateDurationState = (sec: number) => {
    const validSec = Math.max(1, Math.round(sec));
    setDuracaoSegundos(validSec);
    const split = splitDurationToHms(validSec);
    setHmsHours(split.hours);
    setHmsMinutes(split.minutes);
    setHmsSeconds(split.seconds);
  };

  // Load existing appointment or initialize default values
  useEffect(() => {
    if (!isModalOpen) return;

    setConfirmDelete(false);

    if (appointmentId) {
      const appt = appointments.find(a => a.id === appointmentId);
      if (appt) {
        setLeadId(appt.lead_id || '');
        setLeadSearch(appt.lead ? `${appt.lead.nome} (${appt.lead.telefone})` : '');
        setTipo(appt.tipo || 'visita_loja');
        setTitulo(appt.titulo || '');
        
        try {
          const d = new Date(appt.agendado_para);
          setAgendadoPara(format(d, "yyyy-MM-dd'T'HH:mm"));
        } catch {
          setAgendadoPara('');
        }

        const sec = getAppointmentDurationSeconds(appt, 3600);
        updateDurationState(sec);
        
        // Verifica se é uma das durações padrão (15m, 30m, 45m, 60m, 90m, 120m)
        const commonPresets = [900, 1800, 2700, 3600, 5400, 7200];
        setCustomDurationMode(!commonPresets.includes(sec));

        setLojaId(appt.loja_id || '');
        setResponsavelUserId(appt.responsavel_user_id || '');
        setLocal(appt.local || '');
        setObservacoes(appt.observacoes || '');
        setStatus(appt.status || 'agendado');
        return;
      }
    }

    // Default initialization for new appointment
    const targetDate = initialDateTime || new Date();
    setAgendadoPara(format(targetDate, "yyyy-MM-dd'T'HH:mm"));
    setTipo('visita_loja');
    updateDurationState(3600);
    setCustomDurationMode(false);
    setStatus('agendado');
    setObservacoes('');

    const targetLeadObj = initialLead || (targetLeadId ? leads.find(item => item.id === targetLeadId) : null);
    if (targetLeadObj) {
      setLeadId(targetLeadObj.id);
      setLeadSearch(`${targetLeadObj.nome || propLeadName || 'Lead'} (${targetLeadObj.telefone || ''})`);
      setTitulo(`Visita — ${targetLeadObj.nome || propLeadName || 'Lead'}`);
      if (targetLeadObj.loja_id || propLojaId) setLojaId(targetLeadObj.loja_id || propLojaId || '');
    } else if (targetLeadId) {
      setLeadId(targetLeadId);
      setLeadSearch(propLeadName || 'Lead');
      setTitulo(`Visita — ${propLeadName || 'Lead'}`);
      if (propLojaId) setLojaId(propLojaId);
    } else {
      setLeadId('');
      setLeadSearch('');
      setTitulo('');
    }

    if (lojas.length > 0 && !lojaId) {
      setLojaId(propLojaId || lojas[0].id);
      if (lojas[0].endereco) setLocal(lojas[0].endereco);
    }
  }, [isModalOpen, appointmentId, initialDateTime, targetLeadId, initialLead, propLeadName, propLojaId, appointments, leads, lojas]);

  // Auto-set local address when store changes
  useEffect(() => {
    if (lojaId && tipo === 'visita_loja') {
      const selectedLoja = lojas.find(l => l.id === lojaId);
      if (selectedLoja && selectedLoja.endereco) {
        setLocal(`${selectedLoja.nome} - ${selectedLoja.endereco}`);
      }
    } else if (tipo === 'reuniao_online') {
      setLocal('Online (Google Meet)');
    } else if (tipo === 'ligacao') {
      setLocal('Chamada telefônica / WhatsApp');
    }
  }, [lojaId, tipo, lojas]);

  // Lead search filtering
  const filteredLeads = useMemo(() => {
    if (!leadSearch.trim()) return leads.slice(0, 10);
    const q = leadSearch.toLowerCase();
    return leads.filter(l => 
      (l.nome && l.nome.toLowerCase().includes(q)) || 
      (l.telefone && l.telefone.includes(q))
    ).slice(0, 10);
  }, [leads, leadSearch]);

  const handleSelectLead = (lead: any) => {
    setLeadId(lead.id);
    setLeadSearch(`${lead.nome || 'Lead'} (${lead.telefone})`);
    setShowLeadDropdown(false);
    if (!titulo || titulo.startsWith('Visita —')) {
      setTitulo(`Visita — ${lead.nome || 'Lead'}`);
    }
    if (lead.loja_id) {
      setLojaId(lead.loja_id);
    }
  };

  const handlePresetDuration = (seconds: number) => {
    updateDurationState(seconds);
    setCustomDurationMode(false);
  };

  const handleHmsChange = (h: number, m: number, s: number) => {
    const totalSec = parseDurationFromHms(h, m, s);
    setHmsHours(h);
    setHmsMinutes(m);
    setHmsSeconds(s);
    setDuracaoSegundos(Math.max(1, totalSec));
  };

  // Preview de término calculado
  const calculatedEndTime = useMemo(() => {
    if (!agendadoPara) return null;
    try {
      const s = new Date(agendadoPara);
      if (isNaN(s.getTime())) return null;
      return calculateAppointmentEndTime(s, duracaoSegundos);
    } catch {
      return null;
    }
  }, [agendadoPara, duracaoSegundos]);

  const selectedLojaObj = useMemo(() => {
    return lojas.find(l => l.id === lojaId);
  }, [lojas, lojaId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!leadId) {
      toast.error('Por favor, selecione um lead para o agendamento.');
      return;
    }

    if (!agendadoPara) {
      toast.error('Por favor, informe a data e hora do agendamento.');
      return;
    }

    if (duracaoSegundos <= 0 || !Number.isInteger(duracaoSegundos)) {
      toast.error('A duração deve ser um número inteiro de segundos maior que zero.');
      return;
    }

    setLoading(true);

    const payload = {
      lead_id: leadId,
      tipo,
      titulo: titulo.trim() || 'Visita agendada',
      agendado_para: new Date(agendadoPara).toISOString(),
      duracao_segundos: duracaoSegundos,
      duracao_minutos: Math.max(1, Math.round(duracaoSegundos / 60)),
      loja_id: lojaId || null,
      responsavel_user_id: responsavelUserId || null,
      local: local.trim(),
      observacoes: observacoes.trim(),
      status
    };

    try {
      if (appointmentId) {
        const updated = await updateAppointment(appointmentId, payload);
        if (updated) {
          toast.success('Agendamento atualizado com sucesso!');
          if (onSaved) onSaved();
          onClose();
        }
      } else {
        const created = await createAppointment(payload);
        if (created) {
          toast.success('Agendamento criado com sucesso!');
          if (onSaved) onSaved();
          onClose();
        }
      }
    } catch (err: any) {
      console.error('[AppointmentModal] exception', err);
      toast.error(err?.message || 'Erro ao salvar agendamento.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!appointmentId) return;
    setLoading(true);
    try {
      const ok = await deleteAppointment(appointmentId);
      if (ok) {
        toast.success('Agendamento excluído.');
        if (onSaved) onSaved();
        onClose();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao excluir agendamento.');
    } finally {
      setLoading(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div 
        className="bg-white dark:bg-[#111B21] w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-[#202C33]/50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-[#2563EB]/10 dark:bg-blue-500/20 text-[#2563EB] dark:text-blue-400 rounded-xl">
              <CalendarIcon size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {appointmentId ? 'Editar Agendamento' : 'Novo Agendamento na Agenda'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {appointmentId ? 'Atualize horários, duração livre e dados da visita' : 'Defina os detalhes e duração exata para o compromisso'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 flex-1">
          
          {/* Status Chips (If editing) */}
          {appointmentId && (
            <div className="flex items-center gap-1.5 p-2 bg-gray-50 dark:bg-[#202C33] rounded-xl border border-gray-200 dark:border-gray-700">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2">Status:</span>
              {(['agendado', 'confirmado', 'realizado', 'cancelado', 'no_show'] as AppointmentStatus[]).map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatus(st)}
                  className={cn(
                    "px-2.5 py-1 text-xs font-bold rounded-lg capitalize transition-all",
                    status === st
                      ? "bg-[#2563EB] text-white shadow-2xs"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                  )}
                >
                  {st === 'no_show' ? 'Não Compareceu' : st}
                </button>
              ))}
            </div>
          )}

          {/* Lead Search / Select */}
          <div className="relative">
            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
              Lead / Cliente *
            </label>
            <div className="relative">
              <input
                type="text"
                value={leadSearch}
                onChange={(e) => {
                  setLeadSearch(e.target.value);
                  setShowLeadDropdown(true);
                }}
                onFocus={() => setShowLeadDropdown(true)}
                placeholder="Buscar lead por nome ou telefone..."
                className="w-full px-3 py-2 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40"
              />
              {leadId && (
                <CheckCircle size={16} className="absolute right-3 top-3 text-emerald-500" />
              )}
            </div>

            {/* Dropdown Leads */}
            {showLeadDropdown && filteredLeads.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-[#111B21] border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                {filteredLeads.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => handleSelectLead(l)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-[#202C33] border-b border-gray-50 dark:border-gray-800 last:border-0 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{l.nome}</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">{l.telefone}</p>
                    </div>
                    {l.loja_id && (
                      <span className="text-[10px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded font-medium">
                        Loja vinculada
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tipo & Título Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                Tipo
              </label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as AppointmentTipo)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40"
              >
                <option value="visita_loja">🏬 Visita na Loja</option>
                <option value="reuniao_online">💻 Reunião Online</option>
                <option value="ligacao">📞 Ligação</option>
                <option value="outro">📅 Outro</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                Título do Agendamento
              </label>
              <input
                type="text"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: Visita — Mariana Alves"
                className="w-full px-3 py-2 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40"
              />
            </div>
          </div>

          {/* Data/Hora & Início */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                Data e Hora de Início *
              </label>
              <input
                type="datetime-local"
                value={agendadoPara}
                onChange={(e) => setAgendadoPara(e.target.value)}
                required
                className="w-full px-3 py-2 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 font-mono"
              />
            </div>

            {/* Previsão de Término */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                Término Calculado
              </label>
              <div className="px-3 py-2 bg-gray-100/70 dark:bg-[#202C33]/70 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-800 dark:text-gray-200 font-mono flex items-center justify-between">
                <span>
                  {calculatedEndTime ? format(calculatedEndTime, 'dd/MM/yyyy HH:mm:ss') : '--:--'}
                </span>
                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 px-1.5 py-0.5 rounded">
                  {formatDurationLabel(duracaoSegundos)}
                </span>
              </div>
            </div>
          </div>

          {/* [PACOTE D] Duração Livre e Precisa */}
          <div className="p-3.5 bg-gray-50 dark:bg-[#1a252d] border border-gray-200 dark:border-gray-700 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                <Clock size={14} className="text-blue-500" />
                Duração do Agendamento (Livre e Precisa)
              </label>
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                {duracaoSegundos}s ({formatDurationLabel(duracaoSegundos)})
              </span>
            </div>

            {/* Presets Rápidos */}
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: '15m', sec: 900 },
                { label: '30m', sec: 1800 },
                { label: '45m', sec: 2700 },
                { label: '1h', sec: 3600 },
                { label: '1h 30m', sec: 5400 },
                { label: '2h', sec: 7200 }
              ].map((p) => (
                <button
                  key={p.sec}
                  type="button"
                  onClick={() => handlePresetDuration(p.sec)}
                  className={cn(
                    "px-2.5 py-1 text-xs font-bold rounded-lg border transition-all",
                    !customDurationMode && duracaoSegundos === p.sec
                      ? "bg-blue-600 text-white border-blue-600 shadow-2xs"
                      : "bg-white dark:bg-[#202C33] text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-400"
                  )}
                >
                  {p.label}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setCustomDurationMode(true)}
                className={cn(
                  "px-2.5 py-1 text-xs font-bold rounded-lg border transition-all flex items-center gap-1",
                  customDurationMode
                    ? "bg-blue-600 text-white border-blue-600 shadow-2xs"
                    : "bg-white dark:bg-[#202C33] text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-400"
                )}
              >
                <Sparkles size={11} />
                Personalizada
              </button>
            </div>

            {/* Modo Personalizado / Campos HMS */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 border-t border-gray-200 dark:border-gray-700/60">
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Horas</label>
                <input
                  type="number"
                  min={0}
                  value={hmsHours}
                  onChange={(e) => {
                    setCustomDurationMode(true);
                    handleHmsChange(parseInt(e.target.value, 10) || 0, hmsMinutes, hmsSeconds);
                  }}
                  className="w-full px-2.5 py-1.5 text-center text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#202C33] text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Minutos</label>
                <input
                  type="number"
                  min={0}
                  value={hmsMinutes}
                  onChange={(e) => {
                    setCustomDurationMode(true);
                    handleHmsChange(hmsHours, parseInt(e.target.value, 10) || 0, hmsSeconds);
                  }}
                  className="w-full px-2.5 py-1.5 text-center text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#202C33] text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Segundos</label>
                <input
                  type="number"
                  min={0}
                  value={hmsSeconds}
                  onChange={(e) => {
                    setCustomDurationMode(true);
                    handleHmsChange(hmsHours, hmsMinutes, parseInt(e.target.value, 10) || 0);
                  }}
                  className="w-full px-2.5 py-1.5 text-center text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#202C33] text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </div>

          {/* Loja & Responsável Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                Loja
              </label>
              <select
                value={lojaId}
                onChange={(e) => setLojaId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40"
              >
                <option value="">Selecione uma loja...</option>
                {lojas.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                Vendedor / Responsável
              </label>
              <select
                value={responsavelUserId}
                onChange={(e) => setResponsavelUserId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40"
              >
                <option value="">Nenhum responsável atribuído</option>
                {users
                  .filter(u => {
                    if (u.role === 'super_admin' || u.role === 'tenant_admin') return false;
                    if (!lojaId) return true;
                    return u.loja_id === lojaId || !u.loja_id;
                  })
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome} ({u.role})
                    </option>
                  ))
                }
              </select>
            </div>
          </div>

          {/* Local */}
          <div>
            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
              Local do Atendimento
            </label>
            <div className="relative">
              <MapPin size={16} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder="Ex: Av. Silviano Brandão 1066 ou Google Meet"
                className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40"
              />
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
              Observações
            </label>
            <textarea
              rows={3}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Ex: Cliente está procurando sofá retrátil em couro cinza..."
              className="w-full p-3 bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 resize-none"
            />
          </div>

          {/* Confirm Delete Alert */}
          {confirmDelete && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs text-red-800 dark:text-red-300 font-medium">
                <AlertTriangle size={16} className="shrink-0" />
                <span>Tem certeza que deseja excluir este agendamento?</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold"
                >
                  Confirmar
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="px-2.5 py-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-xs font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
            <div>
              {appointmentId && !confirmDelete && (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="px-3 py-2 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 size={14} />
                  Excluir
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
              >
                Cancelar
              </button>
              
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2 bg-[#2563EB] hover:bg-blue-700 text-white font-bold rounded-xl text-sm shadow-sm transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
              >
                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {appointmentId ? 'Atualizar Agendamento' : 'Salvar Agendamento'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
